import { useState, useEffect, useCallback } from 'react';
import { Clock, Loader2, Paperclip, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';
import { formatDateTime } from '../../lib/compose';

const relativeTime = (iso) => {
  const minutes = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (minutes <= 0) return 'birazdan gönderilecek';
  if (minutes < 60) return `${minutes} dakika sonra`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} saat sonra`;
  return `${Math.round(hours / 24)} gün sonra`;
};

const STATUS = {
  pending: { label: 'Bekliyor', cls: 'bg-primary/10 text-primary' },
  sending: { label: 'Gönderiliyor', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' },
  failed: { label: 'Gönderilemedi', cls: 'bg-destructive/10 text-destructive' },
};

// "Göndermeyi zamanla" ile bekleyen e-postalar (IMAP klasörü değil, veritabanı listesi)
export const ScheduledList = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getScheduled();
      setItems(res.scheduled || []);
      setError(null);
    } catch (err) {
      setError(err.message || 'Liste yüklenemedi');
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const cancel = async (item) => {
    const failed = item.status === 'failed';
    if (!window.confirm(failed ? 'Bu kayıt listeden kaldırılsın mı?' : 'Bu zamanlanmış gönderim iptal edilsin mi? E-posta gönderilmeyecek.')) return;
    setBusyId(item.id);
    try {
      await api.cancelScheduled(item.id);
      toast.success(failed ? 'Kayıt kaldırıldı' : 'Gönderim iptal edildi');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error('İşlem başarısız', { description: err.message || 'Bir hata oluştu' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex-1 min-w-0 flex flex-col bg-background overflow-hidden" data-testid="scheduled-list">
      <div className="px-4 sm:px-8 pt-5 pb-4 border-b border-border shrink-0">
        <h2 className="text-xl font-semibold">Zamanlanmış</h2>
        <p className="text-sm text-muted-foreground">
          {items === null ? 'Yükleniyor...' : `${items.length} e-posta gönderilmeyi bekliyor`}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {error ? (
          <div className="p-10 text-center">
            <AlertCircle className="h-8 w-8 mx-auto mb-3 text-destructive" />
            <p className="text-sm text-muted-foreground mb-3">{error}</p>
            <Button variant="outline" size="sm" onClick={load}>Tekrar dene</Button>
          </div>
        ) : items === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mb-3">
              <Clock className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">Zamanlanmış e-posta yok</p>
            <p className="text-xs text-muted-foreground mt-1">Yeni e-postada Gönder düğmesinin yanındaki okla zamanlayabilirsiniz.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border max-w-4xl">
            {items.map((item) => {
              const st = STATUS[item.status] || STATUS.pending;
              return (
                <li key={item.id} className="flex items-start gap-4 px-4 sm:px-8 py-4" data-testid={`scheduled-${item.id}`}>
                  <span className="h-10 w-10 shrink-0 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                    <Clock className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{item.subject || '(Konu yok)'}</p>
                      {item.has_attachments && <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">Alıcı: {item.to.join(', ')}</p>
                    <p className="text-sm mt-1">
                      {formatDateTime(item.send_at)}
                      {item.status === 'pending' && <span className="text-muted-foreground"> · {relativeTime(item.send_at)}</span>}
                    </p>
                    {item.status === 'failed' && item.error && (
                      <p className="text-xs text-destructive mt-1">{item.error}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                    {item.status !== 'sending' && (
                      <Button variant="outline" size="sm" onClick={() => cancel(item)} disabled={busyId === item.id} data-testid={`cancel-scheduled-${item.id}`}>
                        {busyId === item.id && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                        {item.status === 'failed' ? 'Kaldır' : 'İptal et'}
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ScheduledList;
